import { Assembly } from '@/assembly/api/types';
import { Link } from '@/components/link';
import { LocationName } from '@/components/location-name';
import { formatNumber } from '@/utilities/number';
import { Grid, Paper, Typography } from '@mui/material';

interface Props {
  assembly: Assembly;
}

export function AssemblyBuildInformation({ assembly }: Props) {
  const totalParts = assembly.partsUsed.reduce(
    (total, part) => total + part.actualQuantity,
    0
  );

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6}>
        <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Product
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">{assembly.item.name}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Build date
              </Typography>
            </Grid>
            <Grid item xs={6}>
              {assembly.buildDate ? (
                <Typography variant="body2">
                  {assembly.buildDate.toLocaleString({
                    dateStyle: 'medium',
                  })}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Not set
                </Typography>
              )}
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Location
              </Typography>
            </Grid>
            <Grid item xs={6}>
              {assembly.location ? (
                <LocationName location={assembly.location} variant="body2" />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Not set
                </Typography>
              )}
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Internal ID
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">{assembly.internalId}</Typography>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Number of parts
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">{`${formatNumber(totalParts)} (${formatNumber(assembly.partsUsed.length)} SKUs)`}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Matches BOM spec
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">
                {!assembly.hasDeviations ? 'Yes' : 'No'}
              </Typography>
            </Grid>
            {assembly.shipment && (
              <>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Source shipment
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Link
                    to="/shipments"
                    search={{
                      type: 'inbound',
                      status: 'received',
                      query: assembly.shipment.shipmentNumber,
                    }}
                  >
                    {assembly.shipment.shipmentNumber}
                  </Link>
                </Grid>
              </>
            )}
            <Grid item xs={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Intended asset serial
              </Typography>
            </Grid>
            <Grid item xs={6}>
              {assembly.intendedAssetSerial ? (
                <Typography variant="body2">
                  {assembly.intendedAssetSerial}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Not set
                </Typography>
              )}
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Registered asset
              </Typography>
            </Grid>
            <Grid item xs={6}>
              {assembly.asset ? (
                <Link
                  to="/assets/$assetKey"
                  params={{ assetKey: assembly.asset.key }}
                >
                  {assembly.asset.serial}
                </Link>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Not set
                </Typography>
              )}
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
}
